import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { useProperty } from "@/context/PropertyContext";
import { SectionHeader, StatusPill, Loader, Lu, STATUS_META } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

function RoomCard({ r }) {
  const { t } = useLang();
  const m = STATUS_META[r.status];
  return (
    <motion.div layout data-testid={`room-card-${r.number}`}
      className="rounded-xl p-3.5 border relative overflow-hidden cursor-default group"
      style={{ background: m.bg, borderColor: m.border }}
      whileHover={{ y: -3 }} transition={{ duration: 0.2 }}>
      {r.status === "alert" && <span className="absolute top-2 ltr:right-2 rtl:left-2 w-2 h-2 rounded-full bg-rose-500 md-alert-pulse" />}
      <div className="flex items-center justify-between">
        <span className="text-lg font-extrabold font-mono text-foreground">{r.number}</span>
        <Lu name={m.icon} className="w-4 h-4" style={{ color: m.color }} />
      </div>
      <p className="text-[11px] mt-0.5" style={{ color: m.color }}>{t(r.status)}</p>
      {r.type === "premium" && <span className="text-[9px] text-primary font-semibold">★ PREMIUM</span>}
      <div className="mt-2.5 flex items-center gap-2 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-0.5"><Lu name="thermometer" className="w-3 h-3" />{r.temp}°</span>
        <span className="flex items-center gap-0.5"><Lu name={r.ac_on ? "snowflake" : "power"} className="w-3 h-3" style={{ color: r.ac_on ? "#3B82F6" : "#64748B" }} /></span>
        <span className="flex items-center gap-0.5"><Lu name={r.door_locked ? "lock" : "lock-open"} className="w-3 h-3" /></span>
        {r.battery_low && <Lu name="battery-low" className="w-3 h-3 text-rose-400" />}
        {r.lights_on && <Lu name="lightbulb" className="w-3 h-3 text-primary" />}
      </div>
      {r.guest_name && <p className="mt-1.5 text-[11px] text-foreground/80 truncate">{r.guest_name}</p>}
    </motion.div>
  );
}

export default function Rooms() {
  const { t } = useLang();
  const { selectedId } = useProperty();
  const [rooms, setRooms] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!selectedId) return;
    setRooms(null);
    client.get(`/properties/${selectedId}/rooms`).then((r) => setRooms(r.data));
  }, [selectedId]);

  if (!rooms) return <Loader />;
  const filtered = filter === "all" ? rooms : rooms.filter((r) => r.status === filter);
  const floors = [...new Set(rooms.map((r) => r.floor))].sort();
  const counts = { all: rooms.length, ...Object.fromEntries(Object.keys(STATUS_META).map((s) => [s, rooms.filter((r) => r.status === s).length])) };

  return (
    <div>
      <SectionHeader icon="grid-3x3" title={t("rooms")} subtitle={t("liveData")} />
      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList className="bg-foreground/5 border border-border flex-wrap h-auto">
          {["all", ...Object.keys(STATUS_META)].map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`filter-${s}`}
              className="data-[state=active]:bg-primary data-[state=active]:shadow-md data-[state=active]:text-primary-foreground text-foreground/80">
              {t(s)} <span className="ml-1.5 rtl:mr-1.5 font-mono text-xs opacity-70">{counts[s]}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {filter === "all" ? floors.map((f) => (
        <div key={f} className="mb-7">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-foreground/5 flex items-center justify-center text-xs font-mono text-primary">{f}</span>
            {t("floor")} {f}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {rooms.filter((r) => r.floor === f).map((r) => <RoomCard key={r.id} r={r} />)}
          </div>
        </div>
      )) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {filtered.map((r) => <RoomCard key={r.id} r={r} />)}
        </div>
      )}
    </div>
  );
}
